/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DiscussionVisibilityConstants } from "../../../../../../constants";
import { ComponentDynamicPosition } from "../../../../../../models";
import { GetDiscussionThreadsResponse } from "../../../../../../service/query";
import { useAuth } from "../../../../../../useAuth";
import { NewThreadInformation } from "../../../../../../widget/wizards/Discussion/components/NewDiscussion/useNewDiscussion";
import { Message, Thread } from "../../../../../../widget/wizards/models";
import { useActivityWizardContextSwitcher } from "../../../../../shared/utils/activities/useActivityWizardContextSwitcher";

interface UseComponentRowReturnData {
  addDiscussionRef: RefObject<HTMLDivElement>;
  isAddDiscussionVisible: boolean;
  showUnreadIndicator: boolean;
  isExistingDiscussionVisible: boolean;
  canWriteExternalDiscussion: boolean;
  handleOpenExistingDiscussion: () => void;
  handleOpenNewDiscussion: () => void;
  handleCloseDiscussion: () => void;
  newThreadInformation: NewThreadInformation;
  discussionPosition: ComponentDynamicPosition;
  internalThreads: Thread[];
  externalThreads: Thread[];
  setInternalThreads: Dispatch<SetStateAction<Thread[]>>;
  setExternalThreads: Dispatch<SetStateAction<Thread[]>>;
}

export const useComponentRow = (
  discussions: GetDiscussionThreadsResponse[] | undefined,
  componentKey: string,
  dataPath: string,
  isForRepeater?: boolean,
  repeaterIndex?: number
): UseComponentRowReturnData => {
  const { user } = useAuth();
  const userUuid = useMemo(() => user?.userUuid ?? "", [user]);

  const { activityUuid, activityHistoryUuid, canWriteExternalDiscussion } = useActivityWizardContextSwitcher();

  const [isAddDiscussionVisible, setIsAddDiscussionVisible] = useState(false);
  const [isExistingDiscussionVisible, setIsExistingDiscussionVisible] = useState(false);
  const [discussionPosition, setDiscussionPosition] = useState<ComponentDynamicPosition>({ top: 0, left: 0 });

  const [internalThreads, setInternalThreads] = useState<Thread[]>([]);
  const [externalThreads, setExternalThreads] = useState<Thread[]>([]);

  const [showUnreadIndicator, setShowUnreadIndicator] = useState(false);

  const addDiscussionRef = useRef<HTMLDivElement | null>(null);

  const newThreadInformation: NewThreadInformation = {
    componentKey: isForRepeater ? `${componentKey}[${repeaterIndex}]` : componentKey,
    dataPath,
    messageObjectType: "Activity History",
    messageObjectUuid: activityHistoryUuid!,
    messageType: "Comment",
    threadObjectUuid: activityUuid!, // `newThreadInformation` is only used for Discussions inside Edit mode of the wizard, where we will have the activityUuid
  };

  const updateDiscussionPosition = (): void => {
    const castedRef = addDiscussionRef as MutableRefObject<HTMLDivElement | null>;

    setDiscussionPosition({
      top: (castedRef.current?.offsetTop || 0) - 15,
      left: (castedRef.current?.offsetLeft || 0) - 540,
    });
  };

  useLayoutEffect(() => {
    updateDiscussionPosition();

    const handleResize = (): void => updateDiscussionPosition();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [addDiscussionRef, isAddDiscussionVisible, isExistingDiscussionVisible]);

  useEffect(() => {
    const hasUnreadInternalMessages = internalThreads.some(
      (t) => t.messages.length > 0 && t.messages.at(-1)!.createdByUserUuid !== userUuid
    );
    const hasUnreadExternalMessages = externalThreads.some(
      (t) => t.messages.length > 0 && t.messages.at(-1)!.createdByUserUuid !== userUuid
    );
    setShowUnreadIndicator(hasUnreadInternalMessages || hasUnreadExternalMessages);
  }, [discussions, internalThreads, externalThreads, userUuid]);

  useEffect(() => {
    const tempThread: Thread[] = [];
    const tempInternalThreads: Thread[] = internalThreads;
    const tempExternalThreads: Thread[] = externalThreads;

    if (discussions) {
      discussions.forEach((t) => {
        tempThread.push({
          uuid: t.uuid,
          resolved: t.isResolved,
          hasNonDeletedMessagesCreatedByOtherUsers: t.hasNonDeletedMessagesCreatedByOtherUsers,
          messages: t.messages.map((tm) => {
            return {
              uuid: tm.uuid,
              content: tm.content,
              createdAt: tm.createdAt,
              status: tm.status,
              messageSourceOrganisationUuid: tm.sourceOrganisationUuid,
              createdByUserUuid: tm.createdByUser.uuid,
              createdByUserFullName: tm.createdByUser.fullName,
              createdByUserAvatarUrl: tm.createdByUser.avatar?.url,
              rowVersion: tm.rowVersion,
            } as Message;
          }),
        });

        const threadToAdd = tempThread.pop()!; // tempThread.pop() will always return an element because we push to it above

        if (
          t.visibility === DiscussionVisibilityConstants.INTERNAL &&
          !tempInternalThreads.find((temp) => temp.uuid === threadToAdd.uuid)
        ) {
          tempInternalThreads.push(threadToAdd);
        }

        if (
          t.visibility === DiscussionVisibilityConstants.EXTERNAL &&
          !tempExternalThreads.find((temp) => temp.uuid === threadToAdd.uuid)
        ) {
          tempExternalThreads.push(threadToAdd);
        }
      });
    }

    // Sorts the Array, so that unresolved threads are always the first
    tempInternalThreads.sort((a, b) => {
      return Number(a.resolved) - Number(b.resolved);
    });

    tempExternalThreads.sort((a, b) => {
      return Number(a.resolved) - Number(b.resolved);
    });

    // Spread required so that React will always re-render the component
    setInternalThreads([...tempInternalThreads]);
    setExternalThreads([...tempExternalThreads]);
  }, [discussions]);

  const handleOpenExistingDiscussion = (): void => {
    setIsAddDiscussionVisible(true);
  };

  const handleOpenNewDiscussion = (): void => {
    setIsAddDiscussionVisible(true);
  };

  const handleCloseDiscussion = (): void => {
    setIsAddDiscussionVisible(false);
    setIsExistingDiscussionVisible(false);
  };

  return {
    addDiscussionRef,
    isAddDiscussionVisible,
    isExistingDiscussionVisible,
    canWriteExternalDiscussion,
    showUnreadIndicator,
    handleOpenExistingDiscussion,
    handleOpenNewDiscussion,
    handleCloseDiscussion,
    newThreadInformation,
    discussionPosition,
    internalThreads,
    externalThreads,
    setInternalThreads,
    setExternalThreads,
  };
};
